import React, { useState, useContext } from 'react';
import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime } from '@/lib/dates';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const IncidentReportForm = () => {
  const { token, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  const [formData, setFormData] = useState({
    customer_id: '',
    service_location_id: '',
    incident_type: '',
    location_of_incident: '',
    reported_by: user?.name || '',
    position: '',
    date_occurred: '',
    time_occurred: '',
    employee_involved: false,
    employee_names: [],
    witnesses: [],
    item_of_value_involved: false,
    estimated_value: null,
    item_status: null,
    explanation: '',
    supervisor_notified_date: '',
    supervisor_notified_time: '',
    additional_comments: '',
    completed_by_name: user?.name || '',
    completed_by_title: '',
    reviewed_by_name: '',
    reviewed_by_title: '',
    employee_signature: ''
  });

  const [employeeName, setEmployeeName] = useState('');
  const [witnessName, setWitnessName] = useState('');

  React.useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers/names`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchLocations = async (customerId) => {
    try {
      const response = await axios.get(`${API}/service-locations/by-customer/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
    } catch (error) {
      setLocations([]);
    }
  };

  const handleCustomerChange = (customerId) => {
    setSelectedCustomerId(customerId);
    setFormData({ ...formData, customer_id: customerId, service_location_id: '', location_of_incident: '' });
    if (customerId) {
      fetchLocations(customerId);
    } else {
      setLocations([]);
    }
  };

  const handleLocationChange = (locationId) => {
    const location = locations.find(loc => loc.id === locationId);
    if (location) {
      setFormData({
        ...formData,
        service_location_id: locationId,
        location_of_incident: `${location.location_name}, ${location.city}, ${location.state}`
      });
    }
  };

  const addEmployee = () => {
    if (employeeName.trim()) {
      setFormData({
        ...formData,
        employee_names: [...formData.employee_names, employeeName.trim()]
      });
      setEmployeeName('');
    }
  };

  const addWitness = () => {
    if (witnessName.trim()) {
      setFormData({
        ...formData,
        witnesses: [...formData.witnesses, witnessName.trim()]
      });
      setWitnessName('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/incident-reports`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Incident report submitted successfully');
      // Reset form
      setFormData({
        ...formData,
        incident_type: '',
        location_of_incident: '',
        date_occurred: '',
        time_occurred: '',
        employee_involved: false,
        employee_names: [],
        witnesses: [],
        item_of_value_involved: false,
        estimated_value: null,
        item_status: null,
        explanation: '',
        supervisor_notified_date: '',
        supervisor_notified_time: '',
        additional_comments: '',
        reviewed_by_name: '',
        reviewed_by_title: '',
        employee_signature: ''
      });
    } catch (error) {
      toast.error('Failed to submit incident report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* BLACK & WHITE STYLING - NO COLORS PER SPEC */}
      <Card className="mb-6 border-2 border-black">
        <CardHeader className="bg-white border-b-2 border-black">
          <img 
            src="https://customer-assets.emergentagent.com/job_tfs-workspace/artifacts/xuj2h70u_TFSI%203-26%20PNG.png" 
            alt="TFS Logo" 
            className="h-16 mx-auto mb-4 grayscale"
          />
          <CardTitle className="text-2xl text-center text-black">
            INTERNAL INCIDENT REPORT
          </CardTitle>
          <p className="text-center text-black font-medium">
            REPORTE INTERNO DE INCIDENTE
          </p>
          <div className="mt-4 text-sm text-black grid grid-cols-2 gap-2">
            <div>Date of Report: {_fmtDate(new Date())}</div>
            <div>Time of Report: {_fmtDateTime(new Date()).replace(/^\S+\s/, '')}</div>
          </div>
        </CardHeader>
      </Card>

      <form onSubmit={handleSubmit}>
        {/* Section 1: Incident Information */}
        <Card className="mb-6 border-2 border-black">
          <CardHeader className="bg-white border-b-2 border-black">
            <CardTitle className="text-black">Section 1 - Incident Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6 bg-white">
            <div>
              <Label className="text-black font-semibold">Type of Incident / Tipo de Incidente *</Label>
              <Input
                value={formData.incident_type}
                onChange={(e) => setFormData({ ...formData, incident_type: e.target.value })}
                className="border-2 border-black"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-black font-semibold">Customer *</Label>
                <select
                  className="w-full border-2 border-black rounded-md px-3 py-2"
                  value={selectedCustomerId}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                  required
                >
                  <option value="">-- Select Customer --</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>{customer.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-black font-semibold">Service Location / Branch *</Label>
                <select
                  className="w-full border-2 border-black rounded-md px-3 py-2"
                  value={formData.service_location_id}
                  onChange={(e) => handleLocationChange(e.target.value)}
                  disabled={!selectedCustomerId}
                  required
                >
                  <option value="">-- Select Location --</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.location_name} - {location.city}, {location.state}
                    </option>
                  ))}
                </select>
                {!selectedCustomerId && (
                  <p className="text-xs text-gray-600 mt-1">Select customer first</p>
                )}
              </div>
            </div>

            <div>
              <Label className="text-black font-semibold">Location Details / Detalles de Ubicación</Label>
              <Input
                value={formData.location_of_incident}
                onChange={(e) => setFormData({ ...formData, location_of_incident: e.target.value })}
                className="border-2 border-black bg-gray-100"
                placeholder="Auto-filled from location selection"
                readOnly
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-black font-semibold">Reported By / Reportado Por *</Label>
                <Input
                  value={formData.reported_by}
                  onChange={(e) => setFormData({ ...formData, reported_by: e.target.value })}
                  className="border-2 border-black"
                  required
                />
              </div>
              <div>
                <Label className="text-black font-semibold">Position / Posición *</Label>
                <Input
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="border-2 border-black"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-black font-semibold">Date Occurred / Fecha del Incidente *</Label>
                <Input
                  type="date"
                  value={formData.date_occurred}
                  onChange={(e) => setFormData({ ...formData, date_occurred: e.target.value })}
                  className="border-2 border-black"
                  required
                />
              </div>
              <div>
                <Label className="text-black font-semibold">Time Occurred / Hora del Incidente *</Label>
                <Input
                  type="time"
                  value={formData.time_occurred}
                  onChange={(e) => setFormData({ ...formData, time_occurred: e.target.value })}
                  className="border-2 border-black"
                  required
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Employee Involvement */}
        <Card className="mb-6 border-2 border-black">
          <CardHeader className="bg-white border-b-2 border-black">
            <CardTitle className="text-black">Section 2 - Employee Involvement</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6 bg-white">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.employee_involved}
                onChange={(e) => setFormData({ ...formData, employee_involved: e.target.checked })}
                className="rounded border-2 border-black"
              />
              <span className="text-black font-semibold">Did this involve a TFS employee? / ¿Involucró a un empleado?</span>
            </label>

            {formData.employee_involved && (
              <div>
                <Label className="text-black font-semibold">Employee Names / Nombres</Label>
                <div className="flex space-x-2 mb-2">
                  <Input
                    value={employeeName}
                    onChange={(e) => setEmployeeName(e.target.value)}
                    placeholder="Enter employee name"
                    className="border-2 border-black"
                  />
                  <Button type="button" onClick={addEmployee} className="bg-black text-white hover:bg-gray-800">
                    Add
                  </Button>
                </div>
                <ul className="list-disc list-inside text-black">
                  {formData.employee_names.map((name, idx) => (
                    <li key={idx}>{name}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 3: Witnesses */}
        <Card className="mb-6 border-2 border-black">
          <CardHeader className="bg-white border-b-2 border-black">
            <CardTitle className="text-black">Section 3 - Witnesses</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6 bg-white">
            <div>
              <Label className="text-black font-semibold">Witness Names / Nombres del Testigo</Label>
              <div className="flex space-x-2 mb-2">
                <Input
                  value={witnessName}
                  onChange={(e) => setWitnessName(e.target.value)}
                  placeholder="Enter witness name"
                  className="border-2 border-black"
                />
                <Button type="button" onClick={addWitness} className="bg-black text-white hover:bg-gray-800">
                  Add
                </Button>
              </div>
              <ul className="list-disc list-inside text-black">
                {formData.witnesses.map((name, idx) => (
                  <li key={idx}>{name}</li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Items of Value */}
        <Card className="mb-6 border-2 border-black">
          <CardHeader className="bg-white border-b-2 border-black">
            <CardTitle className="text-black">Section 4 - Items of Value</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6 bg-white">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.item_of_value_involved}
                onChange={(e) => setFormData({ ...formData, item_of_value_involved: e.target.checked })}
                className="rounded border-2 border-black"
              />
              <span className="text-black font-semibold">Was an item of value involved? / ¿Artículo de valor?</span>
            </label>

            {formData.item_of_value_involved && (
              <>
                <div>
                  <Label className="text-black font-semibold">Estimated Value ($) / Valor Estimado</Label>
                  <Input
                    type="number"
                    value={formData.estimated_value || ''}
                    onChange={(e) => setFormData({ ...formData, estimated_value: parseFloat(e.target.value) })}
                    className="border-2 border-black"
                  />
                </div>
                <div>
                  <Label className="text-black font-semibold">Item Status / Estado</Label>
                  <select
                    className="w-full border-2 border-black rounded-md px-3 py-2 bg-white"
                    value={formData.item_status || ''}
                    onChange={(e) => setFormData({ ...formData, item_status: e.target.value })}
                  >
                    <option value="">Select...</option>
                    <option value="misplaced">Misplaced / Extraviado</option>
                    <option value="broken">Broken / Roto</option>
                    <option value="thrown_out">Thrown Out / Desechado</option>
                    <option value="recovered">Recovered / Recuperado</option>
                    <option value="n/a">N/A</option>
                  </select>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Section 5: Explanation */}
        <Card className="mb-6 border-2 border-black">
          <CardHeader className="bg-white border-b-2 border-black">
            <CardTitle className="text-black">Section 5 - Explanation of Incident</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 bg-white">
            <Label className="text-black font-semibold">Explanation / Explicación *</Label>
            <textarea
              className="w-full border-2 border-black rounded-md px-3 py-2 min-h-32 bg-white"
              value={formData.explanation}
              onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
              required
            />
          </CardContent>
        </Card>

        {/* Section 6: Supervisor Notification */}
        <Card className="mb-6 border-2 border-black">
          <CardHeader className="bg-white border-b-2 border-black">
            <CardTitle className="text-black">Section 6 - Supervisor Notification</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6 bg-white">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-black font-semibold">Date Notified / Fecha</Label>
                <Input
                  type="date"
                  value={formData.supervisor_notified_date}
                  onChange={(e) => setFormData({ ...formData, supervisor_notified_date: e.target.value })}
                  className="border-2 border-black"
                />
              </div>
              <div>
                <Label className="text-black font-semibold">Time Notified / Hora</Label>
                <Input
                  type="time"
                  value={formData.supervisor_notified_time}
                  onChange={(e) => setFormData({ ...formData, supervisor_notified_time: e.target.value })}
                  className="border-2 border-black"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 8: Signatures */}
        <Card className="mb-6 border-2 border-black">
          <CardHeader className="bg-white border-b-2 border-black">
            <CardTitle className="text-black">Section 8 - Signatures</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6 bg-white">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-black font-semibold">Completed By (Name) *</Label>
                <Input
                  value={formData.completed_by_name}
                  onChange={(e) => setFormData({ ...formData, completed_by_name: e.target.value })}
                  className="border-2 border-black"
                  required
                />
              </div>
              <div>
                <Label className="text-black font-semibold">Title / Título *</Label>
                <Input
                  value={formData.completed_by_title}
                  onChange={(e) => setFormData({ ...formData, completed_by_title: e.target.value })}
                  className="border-2 border-black"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="text-black font-semibold">Employee Signature / Firma del Empleado</Label>
              <Input
                value={formData.employee_signature}
                onChange={(e) => setFormData({ ...formData, employee_signature: e.target.value })}
                placeholder="Type your full name as signature"
                className="border-2 border-black"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-center">
          <Button type="submit" size="lg" disabled={loading} className="bg-black text-white hover:bg-gray-800 px-12">
            {loading ? 'Submitting...' : 'Submit Report / Enviar Reporte'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default IncidentReportForm;
